import { useMemo, useState } from "react";
import { Select, Space } from "antd";
import { useOutletContext } from "react-router-dom";

import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { getActiveProjects } from "@components/Project";

import listProjectInvestments from "./helpers/listProjectInvestments";
import { listProjectBatches } from "./helpers/listProjectFiles";
import type { Batch, Project, Investment, OutletContext } from "./types";

const LABEL_TRANSFER_TO = "Transfer to";
const LABEL_PLACEHOLDER = "Select a project";

type Props = {
  sourceProjectId?: string;
  targetProjectId?: string;
  setTargetBatches: (batches: Batch[]) => void;
  setTargetProjectId: (projectId: string | undefined) => void;
  setTargetInvestments: (investments: Investment[]) => void;
};

const TargetProjectSelect = ({
  sourceProjectId = undefined,
  targetProjectId = undefined,
  setTargetBatches,
  setTargetProjectId,
  setTargetInvestments,
}: Props) => {
  const [ isLoading, setIsLoading ] = useState(false);

  const { request } = useAppContext() as AppContext;
  const { projects } = useOutletContext<OutletContext>();

  const targetProjectOptions = useMemo(() => {
    const activeProjects: Project[] = getActiveProjects(projects);

    const options = activeProjects
      .filter(({ id }) => id !== sourceProjectId)
      .map(({ id, name }) => ({ label: name, value: id }));

    return options;
  }, [ projects, sourceProjectId ]);

  const onChange = async (projectId?: string) => {
    if (!projectId) {
      setTargetProjectId(undefined);
      setTargetInvestments([]);
      return;
    }

    setIsLoading(true);

    const investmentsPromise = listProjectInvestments(request, projectId)
      .then((investments) => setTargetInvestments(investments));

    const batchesPromise = listProjectBatches(request, projectId)
      .then((batch) => setTargetBatches(batch));

    await Promise.all([
      batchesPromise,
      investmentsPromise,
    ]);

    setIsLoading(false);
    setTargetProjectId(projectId);
  };

  return (
    <Space>
      <span>
        {LABEL_TRANSFER_TO}:
      </span>

      <Select
        showSearch
        allowClear
        value={targetProjectId}
        options={targetProjectOptions}
        onChange={onChange}
        placeholder={LABEL_PLACEHOLDER}
        optionFilterProp="label"
        loading={isLoading}
        disabled={isLoading}
      />
    </Space>
  );
};

export default TargetProjectSelect;
